import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { designerApi } from '@/utils/api';

export function isValidModuleKey(key: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(key);
}

function errorDetail(err: unknown): string {
  const e = err as { message?: string; detail?: string; response?: { data?: { detail?: string } } };
  return e?.response?.data?.detail || e?.detail || e?.message || 'Request failed';
}

export interface NewModuleDialogProps {
  open: boolean;
  connectionId: string;
  /** null = creating a new module; a key = editing that module's composition */
  moduleKey: string | null;
  /** current composition when editing (pre-checked) */
  currentObjects: string[];
  /** unassigned object names (selectable) */
  unassigned: string[];
  /** object name -> owning module key, rendered disabled with a hint */
  foreignOwners: Record<string, string>;
  onClose(): void;
  onSaved(moduleKey: string): void;
}

export const NewModuleDialog: React.FC<NewModuleDialogProps> = ({
  open,
  connectionId,
  moduleKey,
  currentObjects,
  unassigned,
  foreignOwners,
  onClose,
  onSaved,
}) => {
  const editMode = moduleKey !== null;
  const [key, setKey] = useState(moduleKey ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set(currentObjects));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKey(moduleKey ?? '');
      setSelected(new Set(currentObjects));
      setError(null);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, moduleKey]);

  const rows = useMemo(() => {
    const selectable = new Set([...currentObjects, ...unassigned]);
    const all = Array.from(selectable).concat(Object.keys(foreignOwners));
    return Array.from(new Set(all)).sort().map((name) => ({
      name,
      owner: foreignOwners[name],
      disabled: !selectable.has(name),
    }));
  }, [currentObjects, unassigned, foreignOwners]);

  const keyValid = isValidModuleKey(key);
  const delta = useMemo(() => {
    const current = new Set(currentObjects);
    const add = Array.from(selected).filter((n) => !current.has(n)).sort();
    const remove = Array.from(current).filter((n) => !selected.has(n)).sort();
    return { add, remove };
  }, [selected, currentObjects]);
  const canSave =
    keyValid &&
    !saving &&
    (editMode ? delta.add.length + delta.remove.length > 0 : selected.size > 0);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await designerApi.assignModuleObjects({
        connection_id: connectionId,
        module_key: key,
        add: delta.add,
        remove: delta.remove,
      });
      onSaved(key);
    } catch (err: unknown) {
      setError(errorDetail(err));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editMode ? 'Manage objects' : 'New module'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Module key"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={editMode}
          fullWidth
          margin="normal"
          error={key.length > 0 && !keyValid}
          helperText={
            key.length > 0 && !keyValid
              ? 'Lowercase letters, digits and underscores; must start with a letter'
              : 'Registry-wide unique identifier, e.g. my_warehouse'
          }
          inputProps={{ 'data-testid': 'module-key-input' }}
        />
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Objects in this module
        </Typography>
        {rows.length === 0 ? (
          <Alert severity="info">No objects available in this connection yet.</Alert>
        ) : (
          <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {rows.map((row) => (
              <ListItem
                key={row.name}
                disablePadding
                onClick={() => !row.disabled && toggle(row.name)}
                sx={{ cursor: row.disabled ? 'default' : 'pointer' }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selected.has(row.name)}
                    disabled={row.disabled}
                    inputProps={{ 'aria-label': row.name }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={row.name}
                  secondary={row.disabled ? `Owned by ${row.owner}` : undefined}
                />
              </ListItem>
            ))}
          </List>
        )}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
